import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Mail,
  Phone,
  Clock,
  FileText,
  Receipt,
  User,
  Calendar,
  Cake,
  Send,
  ExternalLink,
  Loader2,
  Check,
  Edit3,
  Wrench
} from '../../icons/wp-icons';
import { proaktiveAktionenApi } from '../../api/proaktiveAktionenApi';

// Animation-Varianten
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20
  }
};

// Labels für Aktionstypen
const AKTION_TYP_LABELS = {
  KVA_NACHFASSEN: 'Kostenvoranschlag nachfassen',
  RECHNUNG_MAHNUNG: 'Zahlungserinnerung senden',
  KUNDE_INAKTIV: 'Inaktiven Kunden kontaktieren',
  GEBURTSTAG: 'Geburtstagsgrüße senden',
  TERMIN_ERINNERUNG: 'Terminerinnerung',
  WARTUNG_FAELLIG: 'Wartungstermin vereinbaren'
};

// E-Mail Vorlagen
const EMAIL_VORLAGEN = {
  KVA_NACHFASSEN: (aktion) => ({
    betreff: `Rückfrage zu Ihrem Angebot ${aktion.kostenvoranschlag?.nummer || ''}`,
    text: `Guten Tag${aktion.kunde?.name ? ` ${aktion.kunde.name.split(' ').pop()}` : ''},

wir haben Ihnen vor einiger Zeit ein Angebot für Ihre Anfrage zugesendet und wollten nachfragen, ob Sie noch Fragen dazu haben.

Gerne können wir die Details auch telefonisch besprechen oder einen Termin zur Besprechung vereinbaren.

Mit freundlichen Grüßen`
  }),
  RECHNUNG_MAHNUNG: (aktion) => ({
    betreff: `Zahlungserinnerung - Rechnung ${aktion.rechnung?.rechnungsnummer || ''}`,
    text: `Guten Tag${aktion.kunde?.name ? ` ${aktion.kunde.name.split(' ').pop()}` : ''},

bei der Durchsicht unserer Unterlagen ist uns aufgefallen, dass die Rechnung ${aktion.rechnung?.rechnungsnummer || ''} noch offen ist.

Sollte sich die Zahlung mit diesem Schreiben überschneiden, bitten wir Sie, diese Erinnerung als gegenstandslos zu betrachten.

Für Rückfragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen`
  }),
  KUNDE_INAKTIV: (aktion) => ({
    betreff: 'Wir haben Sie vermisst!',
    text: `Guten Tag${aktion.kunde?.name ? ` ${aktion.kunde.name.split(' ').pop()}` : ''},

es ist schon eine Weile her, seit wir zuletzt in Kontakt waren. Wir hoffen, dass es Ihnen gut geht!

Falls Sie wieder Unterstützung benötigen, stehen wir Ihnen jederzeit gerne zur Verfügung.

Mit freundlichen Grüßen`
  }),
  GEBURTSTAG: (aktion) => ({
    betreff: 'Herzlichen Glückwunsch zum Geburtstag!',
    text: `Liebe/r ${aktion.kunde?.name || 'Kunde'},

wir wünschen Ihnen alles Gute zum Geburtstag! Möge das neue Lebensjahr viel Freude und Erfolg bringen.

Herzliche Grüße`
  }),
  TERMIN_ERINNERUNG: (aktion) => ({
    betreff: 'Ihre anstehenden Termine',
    text: `Guten Tag,

hier eine Übersicht Ihrer anstehenden Termine:

${aktion.beschreibung}

Mit freundlichen Grüßen`
  }),
  WARTUNG_FAELLIG: (aktion) => ({
    betreff: `Wartungserinnerung: ${aktion.titel?.replace(' ⚠️', '') || 'Wartung fällig'}`,
    text: `Guten Tag${aktion.kunde?.name ? ` ${aktion.kunde.name.split(' ').pop()}` : ''},

wir möchten Sie daran erinnern, dass eine Wartung für Ihre Anlage fällig ist:

${aktion.beschreibung}

Gerne vereinbaren wir einen passenden Termin mit Ihnen. Bitte melden Sie sich bei uns, damit wir alles für Sie organisieren können.

Mit freundlichen Grüßen`
  })
};

// Button-Konfiguration für Schnellaktionen (verwendet primary-Farbe wie Standard-Buttons)
const SCHNELLAKTION_CONFIG = {
  EMAIL_SENDEN: { icon: Send, label: 'Erinnerung senden' },
  MAHNUNG_SENDEN: { icon: Receipt, label: 'Mahnung senden' },
  ANRUFEN: { icon: Phone, label: 'Jetzt anrufen' },
  SMS_SENDEN: { icon: Send, label: 'SMS senden' },
  GLUECKWUNSCH_SENDEN: { icon: Cake, label: 'Grüße senden' }
};

function ProaktiveAktionModal({ aktion, onClose, onErledigt }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [schnellaktionLoading, setSchnellaktionLoading] = useState(false);
  const [emailVorlage, setEmailVorlage] = useState(null);
  const [bearbeiteterText, setBearbeiteterText] = useState('');
  const [bearbeiteterBetreff, setBearbeiteterBetreff] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [zurueckstellenDatum, setZurueckstellenDatum] = useState('');

  useEffect(() => {
    // E-Mail-Vorlage generieren
    const vorlageFunc = EMAIL_VORLAGEN[aktion.typ];
    if (vorlageFunc) {
      const vorlage = vorlageFunc(aktion);
      setEmailVorlage(vorlage);
      setBearbeiteterBetreff(vorlage.betreff);
      setBearbeiteterText(vorlage.text);
    }

    // Standard-Datum für Zurückstellen (in 3 Tagen)
    const in3Tagen = new Date();
    in3Tagen.setDate(in3Tagen.getDate() + 3);
    setZurueckstellenDatum(in3Tagen.toISOString().split('T')[0]);
  }, [aktion]);

  const handleAusfuehren = async () => {
    try {
      setLoading(true);
      await proaktiveAktionenApi.ausfuehren(aktion.id);
      onErledigt(aktion.id);
    } catch (error) {
      console.error('Fehler beim Ausführen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnorieren = async () => {
    try {
      setLoading(true);
      await proaktiveAktionenApi.ignorieren(aktion.id);
      onErledigt(aktion.id);
    } catch (error) {
      console.error('Fehler beim Ignorieren:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleZurueckstellen = async () => {
    if (!zurueckstellenDatum) return;
    try {
      setLoading(true);
      await proaktiveAktionenApi.zurueckstellen(aktion.id, new Date(zurueckstellenDatum).toISOString());
      onErledigt(aktion.id);
    } catch (error) {
      console.error('Fehler beim Zurückstellen:', error);
    } finally {
      setLoading(false);
    }
  };

  // Schnellaktion ausführen (E-Mail senden, Mahnung erstellen, etc.)
  const handleSchnellAktion = async (data = {}) => {
    try {
      setSchnellaktionLoading(true);
      const response = await proaktiveAktionenApi.schnellAktion(aktion.id, data);

      if (response.data.success) {
        // Bei OPEN_PHONE: Telefon-Link öffnen
        if (response.data.action === 'OPEN_PHONE' && response.data.telefon) {
          window.location.href = `tel:${response.data.telefon}`;
        } else {
          // Aktion erfolgreich - Modal schließen und Liste aktualisieren
          onErledigt(aktion.id);
        }
      } else {
        alert(response.data.message || 'Aktion konnte nicht ausgeführt werden');
      }
    } catch (error) {
      console.error('Fehler bei Schnellaktion:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Ausführen der Aktion';
      alert(errorMsg);
    } finally {
      setSchnellaktionLoading(false);
    }
  };

  const handleKundeOeffnen = () => {
    if (aktion.kundeId) {
      navigate(`/kunden/${aktion.kundeId}`);
      onClose();
    }
  };

  const handleRechnungOeffnen = () => {
    if (aktion.rechnungId) {
      navigate(`/rechnungen/${aktion.rechnungId}`);
      onClose();
    }
  };

  const handleKVAOeffnen = () => {
    if (aktion.kostenvoranschlagId) {
      navigate(`/kostenvoranschlaege/${aktion.kostenvoranschlagId}`);
      onClose();
    }
  };

  const formatBetrag = (cents) => {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  };

  const formatDatum = (datum) => {
    return new Date(datum).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Animated Backdrop */}
        <motion.div
          className="fixed inset-0 bg-black/50"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ duration: 0.15, ease: "easeOut" }}
          onClick={onClose}
        />

        {/* Modal Container */}
        <div className="flex min-h-full items-center justify-center p-4">
          {/* Animated Modal Content */}
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {AKTION_TYP_LABELS[aktion.typ] || 'Aktion ausführen'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Kontext-Informationen */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {/* Kunde */}
                {aktion.kunde && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Kunde:</span>
                      <span className="font-medium">{aktion.kunde.name}</span>
                    </div>
                    <button
                      onClick={handleKundeOeffnen}
                      className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
                    >
                      Öffnen <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* KVA Details */}
                {aktion.kostenvoranschlag && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">KVA:</span>
                      <span className="font-medium">#{aktion.kostenvoranschlag.nummer}</span>
                      <span className="text-sm text-gray-500">
                        ({formatBetrag(aktion.kostenvoranschlag.bruttogesamt)})
                      </span>
                    </div>
                    <button
                      onClick={handleKVAOeffnen}
                      className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
                    >
                      Öffnen <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Rechnungs-Details */}
                {aktion.rechnung && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">Rechnung:</span>
                      <span className="font-medium">#{aktion.rechnung.rechnungsnummer}</span>
                      <span className="text-sm text-gray-500">
                        ({formatBetrag(aktion.rechnung.bruttogesamt)})
                      </span>
                    </div>
                    <button
                      onClick={handleRechnungOeffnen}
                      className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
                    >
                      Öffnen <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Erstellt am */}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  Erstellt am: {formatDatum(aktion.erstelltAm)}
                </div>
              </div>

              {/* Beschreibung */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Beschreibung</h3>
                <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 text-sm">
                  {aktion.beschreibung}
                </p>
              </div>

              {/* E-Mail Vorlage (für E-Mail-Aktionen) */}
              {emailVorlage && (aktion.vorgeschlageneAktion === 'EMAIL_SENDEN' ||
                aktion.vorgeschlageneAktion === 'MAHNUNG_SENDEN' ||
                aktion.vorgeschlageneAktion === 'GLUECKWUNSCH_SENDEN' ||
                aktion.vorgeschlageneAktion === 'WARTUNG_TERMIN_VEREINBAREN') && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Vorgeschlagene E-Mail</h3>
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
                    >
                      <Edit3 className="w-4 h-4" />
                      {editMode ? 'Vorschau' : 'Bearbeiten'}
                    </button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    {/* Betreff */}
                    <div className="border-b px-3 py-2 bg-gray-50">
                      <label className="text-xs text-gray-500">Betreff:</label>
                      {editMode ? (
                        <input
                          type="text"
                          value={bearbeiteterBetreff}
                          onChange={(e) => setBearbeiteterBetreff(e.target.value)}
                          className="w-full mt-1 px-2 py-1 border rounded text-sm"
                        />
                      ) : (
                        <p className="text-sm font-medium">{bearbeiteterBetreff}</p>
                      )}
                    </div>

                    {/* Text */}
                    <div className="p-3">
                      {editMode ? (
                        <textarea
                          value={bearbeiteterText}
                          onChange={(e) => setBearbeiteterText(e.target.value)}
                          rows={8}
                          className="w-full px-2 py-1 border rounded text-sm resize-none"
                        />
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {bearbeiteterText}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Kontakt-Optionen (für Anruf-Aktionen) */}
              {aktion.vorgeschlageneAktion === 'ANRUFEN' && aktion.kunde?.telefon && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Kontakt</h3>
                  <a
                    href={`tel:${aktion.kunde.telefon}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                  >
                    <Phone className="w-4 h-4" />
                    {aktion.kunde.telefon}
                  </a>
                </div>
              )}

              {/* Zurückstellen Option */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Zurückstellen bis</h3>
                <input
                  type="date"
                  value={zurueckstellenDatum}
                  onChange={(e) => setZurueckstellenDatum(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t p-4 bg-gray-50 shrink-0 space-y-3">
              {/* Wartung: Zwei Aktions-Buttons (Email + Telefon) */}
              {aktion.vorgeschlageneAktion === 'WARTUNG_TERMIN_VEREINBAREN' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSchnellAktion({ kanal: 'email' })}
                    disabled={schnellaktionLoading || loading}
                    className={`flex-1 bg-primary text-white px-4 py-3 rounded-button font-medium hover:bg-primary-dark transition flex items-center justify-center gap-2 ${schnellaktionLoading || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {schnellaktionLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                    Per Email erinnern
                  </button>
                  <button
                    onClick={() => handleSchnellAktion({ kanal: 'telefon' })}
                    disabled={schnellaktionLoading || loading}
                    className={`flex-1 bg-gray-600 text-white px-4 py-3 rounded-button font-medium hover:bg-gray-700 transition flex items-center justify-center gap-2 ${schnellaktionLoading || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {schnellaktionLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Phone className="w-5 h-5" />
                    )}
                    Anrufen lassen
                  </button>
                </div>
              )}

              {/* Schnellaktion Button - für andere Aktionstypen */}
              {SCHNELLAKTION_CONFIG[aktion.vorgeschlageneAktion] && aktion.vorgeschlageneAktion !== 'WARTUNG_TERMIN_VEREINBAREN' && (
                <button
                  onClick={() => handleSchnellAktion()}
                  disabled={schnellaktionLoading || loading}
                  className={`w-full bg-primary text-white px-4 py-3 rounded-button font-medium hover:bg-primary-dark transition flex items-center justify-center gap-2 ${schnellaktionLoading || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {schnellaktionLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    (() => {
                      const Icon = SCHNELLAKTION_CONFIG[aktion.vorgeschlageneAktion].icon;
                      return <Icon className="w-5 h-5" />;
                    })()
                  )}
                  {SCHNELLAKTION_CONFIG[aktion.vorgeschlageneAktion].label}
                </button>
              )}

              {/* Sekundäre Aktionen */}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={handleIgnorieren}
                  disabled={loading || schnellaktionLoading}
                  className="w-full sm:w-auto px-4 py-2.5 text-gray-600 hover:bg-gray-200 rounded-lg transition text-sm order-3 sm:order-1"
                >
                  Ignorieren
                </button>

                <button
                  onClick={handleZurueckstellen}
                  disabled={loading || schnellaktionLoading}
                  className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg transition text-sm flex items-center justify-center gap-2 order-2"
                >
                  <Clock className="w-4 h-4" />
                  Zurückstellen
                </button>

                <button
                  onClick={handleAusfuehren}
                  disabled={loading || schnellaktionLoading}
                  className="w-full sm:w-auto px-4 py-2.5 bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition text-sm flex items-center justify-center gap-2 order-1 sm:order-3"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Als erledigt markieren
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

export default ProaktiveAktionModal;
